import React, { useState, useEffect } from 'react';
import './HUDHeader.css';
import Prompt from '../Prompt/Prompt';
import { useWebSocket } from '../../context/WebSocketContext';

const HUDHeader = ({hasStopButton = false}) => {
    const [timeRemaining, setTimeRemaining] = useState(60 * 60); // Timer starts at 60 minutes (in seconds)
    const [timeSpent, setTimeSpent] = useState(0); // Time used (in seconds)
    const [isRunning, setIsRunning] = useState(false); // Control the state of the timer
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isStopModalOpen, setIsStopModalOpen] = useState(false);
    const [isStartModalOpen, setIsStartModalOpen] = useState(false);
    const [initialSync, setInitialSync] = useState(false);
    const {
        sendStartTimer,
        getTimerStatus,
        sendPauseTimer,
        sendModifyTimer,
        sendResetTimer,
        sendStopGame,
        sendStartGame,
        elapsedTime,
        timeLeft,
        timerIsRunning,
        configs,
        handleConfigChange,
        selectedConfig,
        gameLanguage
    } = useWebSocket();

    // Timer interval for countdown and duration tracking
    useEffect(() => {
        let timerInterval = null;

        if (isRunning) {
            timerInterval = setInterval(() => {
                setTimeRemaining((prev) => (prev > 0 ? prev - 1 : 0)); // Decrease time remaining
                setTimeSpent((prev) => prev + 1); // Increase time spent
            }, 1000); // Update every second
        } else {
            clearInterval(timerInterval); // Clear interval when stopped
        }

        // Cleanup on component unmount or when timer stops
        return () => clearInterval(timerInterval);
    }, [isRunning]);

    useEffect(() => {
        const handleElapsedTimeUpdate = () => {
            setIsRunning(timerIsRunning);
            if(timeLeft > 0) {
                setTimeRemaining(Math.round(timeLeft));
            } else {
                setTimeRemaining(0);
            }
            if(elapsedTime || elapsedTime === 0) {
                setInitialSync(true);
                setTimeSpent(Math.round(parseFloat(elapsedTime)));
            }
        };

        handleElapsedTimeUpdate();
    }, [elapsedTime]);

    useEffect(() => {
        // Set up an interval to call getTimerStatus() every x seconds
        const interval = setInterval(() => {
            getTimerStatus();
        }, 60000);

        // Cleanup the interval on component unmount
        return () => clearInterval(interval);
    }, [getTimerStatus]);


    // Format time from seconds to MM:SS
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
    };

    // Start the timer
    const startTimer = () => {
        setIsRunning(true);
        sendStartTimer();
        getTimerStatus();
    };

    // Stop the timer
    const stopTimer = () => {
        setIsRunning(false)
        sendPauseTimer();
    };

    // Stop the game and play the end video
    const stopGame = () => {
        sendStopGame()
        setIsStopModalOpen(false);
    };

    // Stop the game and play the end video
    const startGame = (language) => {
        sendStartGame(language)
        setIsStartModalOpen(false);
    };

    // Reset the timer to 60 minutes
    const resetTimer = () => {
        setIsRunning(false);
        setTimeRemaining(60 * 60); // Reset time to 60 minutes
        setTimeSpent(0); // Reset spent time
        sendResetTimer();
    };

    const handleAddTime = (minutesToAdd) => {
        setTimeRemaining((prev) => prev + minutesToAdd * 60);
        setIsModalOpen(false); // Close the modal after submission
        sendModifyTimer(minutesToAdd);
    };

    return (
        <div className="header-controls">
            {hasStopButton && (
                <div className="header-row">
                    <div className="left-actions">
                        <button
                            className="game-button"
                            aria-label="Start spil"
                            onClick={() => setIsStartModalOpen(true)}
                        >
                            <span>Start</span>
                        </button>
                    </div>

                    <div className="right-danger">
                        <button
                            className="game-button danger"
                            aria-label="Stop spil"
                            onClick={() => setIsStopModalOpen(true)}
                        >
                            <span>Stop</span>
                        </button>
                    </div>
                </div>
            )}
                {gameLanguage  && (
                    <div className="header-language">
                        language: { gameLanguage.toUpperCase() }
                    </div>
                )}
            <h3>Game Control Area</h3>
            <div className={`timer ${!initialSync ? 'blinking' : ''}`}>
                <p>Tid tilbage: </p>
                <span className="countdown">{formatTime(timeRemaining)}</span>
                <p>Tid brugt: </p>
                <span className="duration">{formatTime(timeSpent)}</span>
            </div>
            <div className={`timer-buttons  ${!initialSync ? 'blinking' : ''}`}>
                <button onClick={startTimer} className={`icon-only-button ${isRunning ? 'active' : ''}`}>
                    <img src={process.env.PUBLIC_URL + '/play.svg'} alt="Play" />
                </button>
                <button onClick={stopTimer} className={`icon-only-button ${!isRunning ? 'active' : ''}`}>
                    <img src={process.env.PUBLIC_URL + '/pause.svg'} alt="Pause" />
                </button>
                <button onClick={resetTimer} className="icon-only-button">
                    <img src={process.env.PUBLIC_URL + '/rotate-ccw.svg'} alt="Stop" />
                </button>
                <button onClick={() => setIsModalOpen(true)} className="icon-only-button">
                    <img src={process.env.PUBLIC_URL + '/plus.svg'} alt="Add" />
                </button>
            </div>
            {/* Dropdown for Configurations */}
            {configs && configs.length > 0 && (
                <div className={`game-config-container`} style={{width: '95%'}}>
                    <select
                        id="game-config"
                        value={selectedConfig}
                        onChange={(e) => handleConfigChange(e.target.value)}
                    >
                        <option value="" disabled>
                            Select a configuration
                        </option>
                        {configs.map((config, _) => (
                            <option key={config} value={config}>
                                {config ? config.charAt(0).toUpperCase() + config.slice(1) : ''}
                            </option>
                        ))}
                    </select>
                </div>
            )}
                <Prompt
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleAddTime}
                    title={"Tilføj minuttal"}
                    placeholder={"Antal minutter"}
                    confirmText={"Bekræft"}
                />
                <Prompt
                    isOpen={isStopModalOpen}
                    onClose={() => setIsStopModalOpen(false)}
                    onSubmit={stopGame}
                    danger={true}
                    hasInput={false}
                    title={"Stop spil og afspil slut video"}
                    confirmText={"Bekræft"}
                />
                <Prompt
                    isOpen={isStartModalOpen}
                    onClose={() => setIsStartModalOpen(false)}
                    onSubmit={startGame}
                    hasInput={false}
                    title={"Vælg sprog og start spillet"}
                    confirmText={"Bekræft"}
                    hasLanguageSelector={true}
                    languageOptions={['dansk', 'english']}
                />
        </div>
    );
};

export default HUDHeader;